/* eslint-disable max-lines */
/* eslint-disable max-statements */
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { getRevisionCodes } from 'services/revisionCodes.service';
import {
  callApiAndUpdateState,
  useApiStateRecord
} from '@exxonmobil/uit-uda-ui-components-lib';
import { getWorkCenters } from 'services/workcenters.service';
import { WorkCenter } from 'models/workcenters';
import { UiCommands } from 'pages/Incrementals/Commands/UiCommands';
import { UiViewModelState } from 'pages/Incrementals/UiState';
import { UiQueries } from 'pages/Incrementals/Queries/UiQueries';
import { ApiWrappers } from 'pages/Incrementals/Commands/apiWrapper';
import { ApiModelState } from 'pages/Incrementals/apiModelState';
import { isApprover } from './Provider.helpers';
import {
  initStateDataValue,
  IncrementalProviderValueType,
  defaultWorkCentersState
} from './IncrementalProvider.helpers';

/* ******************************* Provider Component************************************ */
type IncrementalProviderCompProps = {
  children: ReactElement;
};

const context =
  React.createContext<IncrementalProviderValueType>(initStateDataValue);

const renderProviderComponent = (
  children: ReactElement,
  uiState: UiViewModelState,
  uiCommands: UiCommands,
  uiQueries: UiQueries
) => {
  const contextValue: IncrementalProviderValueType = useMemo(
    () => ({
      uiState,
      uiCommands,
      uiQueries
    }),
    [uiState]
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

const IncrementalProviderComp = ({
  children
}: IncrementalProviderCompProps): ReactElement => {
  const isUserApprover = isApprover();

  // const revisionCodesStateRecord = useApiStateRecord<RevisionCode[]>(
  //   defaultRevisionCodesState.data
  // );

  const workCentersStateRecord = useApiStateRecord<WorkCenter[]>(
    defaultWorkCentersState.data
  );

  // const revisionCodeIncrementalsStateRecord = useApiStateRecord<
  //   IncrementalWorkCenter[]
  // >(defaultIncrementalsState.data);

  // const revisionCodeBaselinesStateRecord = useApiStateRecord<
  //   BaselineWorkCenterDays[]
  // >(defaultBaselinesState.data);

  // const revisionCodeRequiredStateRecord = useApiStateRecord<
  //   RequiredWorkCenter[]
  // >(defaultRequiredState.data);

  const [uiState, setUiState] = useState<UiViewModelState>({
    isScreenLoading: true,
    screenError: null,
    justificationReasons: [],
    companies: [],
    workCenters: [],
    revisionCodes: [],
    workCenterFilter: null,
    currentRevisionCode: null,
    currentWeekId: null,
    weeks: [],
    isUserApprover
  });

  const [apiModelState, setApiModelState] = useState<ApiModelState>({
    allRevisionCodes: [],
    allWorkCenters: [],
    revisionCodeBaselines: [],
    revisionCodeIncrementals: [],
    revisionCodeRequired: []
  });

  const setUiStateTriggerReRender = (state: UiViewModelState) => {
    // If setting the same in memory object, it would not cause a re-render
    setUiState({ ...state });
  };

  const getWorkCentersCallback = useCallback(async () => {
    return callApiAndUpdateState({
      stateRecord: workCentersStateRecord,
      apiServiceFunction: getWorkCenters
    });
  }, []);

  const uiQueries = new UiQueries(uiState);

  const getWorkCentersWrapper = async (): Promise<WorkCenter[]> => {
    await getWorkCentersCallback();
    if (
      workCentersStateRecord.state.responseStatus !== 0 &&
      workCentersStateRecord.state.responseStatus !== 200
    ) {
      throw new Error(workCentersStateRecord.state.error);
    }
    return workCentersStateRecord.state.data;
  };

  const apiWrappers: ApiWrappers = {
    getWorkCenters: getWorkCentersWrapper,
    getRevisionCodes
  };

  const uiCommands = new UiCommands(
    uiState,
    uiQueries,
    setUiStateTriggerReRender,
    apiWrappers,
    apiModelState,
    (apiState: ApiModelState) => setApiModelState(apiState)
  );

  useEffect(() => {
    uiCommands.initializeScreen();
  }, []);

  return renderProviderComponent(children, uiState, uiCommands, uiQueries);
};

export const IncrementalProvider = IncrementalProviderComp;
export const IncrementalContext = context;
